import { memo } from "react";

import { Image } from "@react/components/Image";
import { Text } from "@react/components/Text";
import {
  type CustomMetaobjectValueFieldType,
  type StandardizedSizeGuideData,
} from "@tablescape/state";
import { formatKeyToLabel } from "@tablescape/utilities/general";
import { tw, type TTailwindString } from "classnames";

const tableCellStyleBase = tw(
  "p-0",
  "px-2",
  "py-3",
  "whitespace-nowrap",
  "border-none"
);

const tableRowStyleBase = tw(
  "border-0",
  "border-b",
  "border-solid",
  "border-char-200"
);

const styles = {
  table: tw("w-full", "border-none", "p-0", "border-collapse"),
  bodyRow: tableRowStyleBase,
  headRow: tw(tableRowStyleBase, "bg-bone-200"),
  defaultCell: tw(tableCellStyleBase, "text-center"),
  renderedCellContent: tw("flex", "flex-row", "flex-nowrap", "justify-center"),
  headerCell: tw(tableCellStyleBase, "w-30"),
  renderedImage: tw("inline-block", "!w-5" as TTailwindString),
  renderedText: tw("mx-2"),
};

export type SizeGuideTableProps = {
  fields: string[];
  optionLabels: string[];
  tableLabel: string;
  values: StandardizedSizeGuideData[][];
};

const SizeGuideTableBase = ({
  fields,
  optionLabels,
  tableLabel,
  values,
}: SizeGuideTableProps) => {
  const cellContentGenerator = (
    value: string | number | string[] | undefined,
    fieldType: CustomMetaobjectValueFieldType
  ) => {
    if (typeof value === "string" || typeof value === "number") {
      return fieldType === "image" && value ? (
        <Image
          className={styles.renderedImage}
          data={{ url: value.toString() }}
          key={fieldType}
        />
      ) : (
        <Text as="body2" className={styles.renderedText} key={fieldType}>
          {fieldType === "text" ? formatKeyToLabel(value.toString()) : value}
        </Text>
      );
    }
  };

  const renderedCellGenerator = (
    sizeGuideMetaobject: StandardizedSizeGuideData,
    className: TTailwindString,
    key: string
  ) => {
    if (sizeGuideMetaobject.type === "dimensions") {
      return sizeGuideMetaobject.dimensionsDataFields.map((field, index) => (
        <td
          className={className}
          key={`${key}-dimensions-field-${index}-${sizeGuideMetaobject.fields[field]}`}
        >
          {cellContentGenerator(sizeGuideMetaobject.fields[field], "text")}
        </td>
      ));
    }
    const customDataFields = sizeGuideMetaobject.customDataFields;
    const valueKey =
      customDataFields &&
      customDataFields
        .map((valueField) => {
          return sizeGuideMetaobject.fields[valueField];
        })
        .join("-");
    return (
      <td className={className} key={`${key}-${valueKey}`}>
        <div className={styles.renderedCellContent}>
          {customDataFields &&
            customDataFields.map((valueField: CustomMetaobjectValueFieldType) =>
              cellContentGenerator(
                valueField === "image"
                  ? sizeGuideMetaobject.customDataImage
                  : sizeGuideMetaobject.fields[valueField],
                valueField
              )
            )}
        </div>
      </td>
    );
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr className={styles.headRow}>
          <td className={styles.headerCell} key={`row-head-${tableLabel}`}>
            {cellContentGenerator(tableLabel, "text")}
          </td>
          {fields.map((field) => (
            <td className={styles.defaultCell} key={`row-head-${field}`}>
              {cellContentGenerator(field, "text")}
            </td>
          ))}
        </tr>
      </thead>
      <tbody>
        {optionLabels.map((label: string, index) => {
          return (
            <tr className={styles.bodyRow} key={label}>
              <td className={styles.headerCell} key={`row-${index}-${label}`}>
                {cellContentGenerator(label, "text")}
              </td>
              {values[index].map((sizeGuideMetaobject, cellIndex) =>
                renderedCellGenerator(
                  sizeGuideMetaobject,
                  styles.defaultCell,
                  `row-${index}-cell-${cellIndex}`
                )
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const SizeGuideTable = memo(SizeGuideTableBase);
